import { useDispatch, useSelector, useStore } from 'react-redux';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box, Stack, SxProps, Theme, Typography } from '@mui/material';
import Header from './header';
import WelcomePage from './welcome-page';
import Footer from './footer';
import ActiveChat from 'app/concierge/panel/active-chat';
import ClarificationQuestionComponent from 'app/concierge/panel/clarification-question/ClarificationQuestion';
import { ClarificationQuestionItem } from 'app/concierge/types';
import { findClarificationBlock, getClarificationQuestions } from 'app/concierge/tools/clarification';
import RecentChats from 'app/concierge/panel/recent-chats';
import { Resizable } from 're-resizable';
import {
    closeChat as setCloseChat,
    setConciergeWidgetWidth,
    setSelectedDomains,
    setCustomStyles,
    setIsAppPanel,
    clearNewRequestMessage,
    clearPrependContext,
    clearTestSession,
    setDefaultSelectedDomains,
} from 'store/conciergeSlice';
import { hide, clearNewChat, setFromSearch } from 'store/conciergeSlice';
import type { IConciergeIssuesTarget } from 'store/conciergeSlice';
import { conciergeAPI } from 'api/concierge';
import type { IRecentChat, ITestSession } from 'api/concierge';
import toast from 'app/toast';
import { transcriptToDialog } from 'app/concierge/tools/transcriptToDialog';
import { setWaitingConcierge } from 'store/globalSearchSlice';
import { mergeCustomStyles } from 'app/concierge/tools/mergeCustomStyles';
import { tokenFormUpdatePropertyValue, uid } from 'tools/tools';
import { useConciergeChat } from '../hooks/useConciergeChat';
import { useHasConciergeDomains } from '../hooks/useHasConciergeDomains';
import { testSessionScopeParams, useOpenTestSession } from '../hooks/useOpenTestSession';
import IconMi from 'components/common/icon/IconMi';
import useBundleTranslation from 'i18n';
import { useEntityIssues } from 'hooks/useEntityIssues';
import { dispatchCustomEvent } from 'tools/events';
import type { RootState } from 'store/store';
import ReportConciergeContent from 'app/extreport/concierge/ReportConciergeContent';
import PanelInsightCard from 'app/extreport/concierge/PanelInsightCard';
import ClarificationsSection from 'components/common/clarifications/ClarificationsSection';
import useClarificationsVisibility from 'components/common/clarifications/useClarificationsVisibility';
import TestSessionBanner from 'app/concierge/panel/test-session/TestSessionBanner';

import defaultStyles from './styles';

export interface ICustomStyles {
    panel?: SxProps<Theme>;
    welcomePage?: SxProps<Theme>;
    header?: SxProps<Theme>;
    footer?: SxProps<Theme>;
    footerDomainSelector?: SxProps<Theme>;
    activeChat?: SxProps<Theme>;
    activeChatMessage?: SxProps<Theme>;
    activeChatMessageResultItem?: SxProps<Theme>;
    recentChats?: SxProps<Theme>;
    recentChatsItem?: SxProps<Theme>;
    recentChatsMenu?: SxProps<Theme>;
}

interface IProps {
    welcomeIcon?: string;
    welcomeIconWidth?: number;
    welcomeIconHeight?: number;
    botIcon?: string;
    title?: string;
    description?: string;
    startNewChatAfter?: number;
    showSourceSelector?: boolean;
    displayModelName?: boolean;
    suggestedPrompts?: string[];
    showSearchRequest?: boolean;
    includeContentSearch?: boolean;
    includeHelpDocs?: boolean;
    includeGlossaryTerms?: boolean;
    includeCustomFaq?: boolean;
    availableDomains?: string[];
    selectedDomains?: string[];
    isAppPanel?: boolean;
    customStyles?: ICustomStyles;
    loadMore?: boolean;
    extraUrlParams?: { [key: string]: string };
    useInfoButton?: boolean;
    hideHeader?: boolean;
    autoFocus?: boolean;
    getContext?: () => any | Promise<any>;
    customInstructions?: {
        mcp_post_processing?: string;
        llm_comment?: string;
    };
    onAction?: (
        action: string,
        payload: any,
    ) => Promise<{ success: boolean; message?: string }> | { success: boolean; message?: string };
}

const RESIZE_PAUSE_MS = 100;

export default function ConciergePanel({
    welcomeIcon,
    title,
    description,
    suggestedPrompts,
    botIcon,
    startNewChatAfter,
    displayModelName,
    showSourceSelector,
    welcomeIconHeight,
    welcomeIconWidth,
    showSearchRequest,
    availableDomains,
    includeContentSearch,
    includeCustomFaq,
    includeGlossaryTerms,
    includeHelpDocs,
    selectedDomains,
    isAppPanel,
    customStyles,
    loadMore,
    extraUrlParams,
    useInfoButton = false,
    hideHeader = false,
    autoFocus = true,
    getContext,
    customInstructions,
    onAction,
}: IProps) {
    const dispatch = useDispatch();
    const { t } = useBundleTranslation(['app/concierge/widget']);

    const styles = useMemo(() => {
        return mergeCustomStyles(defaultStyles, customStyles?.panel, 'container');
    }, [customStyles]);

    const visible = useSelector((state: any) => state.concierge.visible);
    // Read without subscribing: the panel only needs the width as its starting size when it opens, and
    // re-rendering the chat on a mid-drag width update would only lengthen that update.
    const store = useStore<RootState>();
    const issuesTarget = useSelector((state: any) => state.concierge.issuesTarget as IConciergeIssuesTarget | null);

    const {
        context: issuesContext,
        loading: issuesLoading,
        answerQuestion,
        skipQuestion,
    } = useEntityIssues(issuesTarget?.id ?? 0, issuesTarget?.type ?? 'element', visible && !!issuesTarget);
    const newChat = useSelector((state: any) => state.concierge.newChat);
    const newRequestMessage = useSelector((state: any) => state.concierge.newRequestMessage);
    const prependContext = useSelector((state: any) => state.concierge.prependContext);
    const closeChat = useSelector((state: any) => state.concierge.closeChat);
    const testSession = useSelector((state: any) => state.concierge.testSession as ITestSession | null);

    const [activeTab, setActiveTab] = useState('concierge');
    const defaultSelectedDomains = useSelector(
        (state: any) => state.concierge.defaultSelectedDomains as string[] | null | undefined,
    );
    // WelcomePage resets defaultSelectedDomains to null on unmount, which empties its examples; leaving for
    // Recent Chats unmounts it, so the value is kept here and put back on return.
    const welcomeDomainsRef = useRef<{ value: string[] | undefined } | null>(null);
    const [answeredQuestionIds, setAnsweredQuestionIds] = useState<string[]>([]);

    const { hasDomains, isLoading: isDomainsLoading } = useHasConciergeDomains();

    const {
        dialog,
        setDialog,
        loading,
        activeChat,
        setActiveChat,
        onRequestSubmit,
        startNewChat: startNewChatBase,
        onAbort,
        domainsData,
        toolsData,
        onResendRequest,
        onRetryError,
        setLastInteraction,
        clearActiveChatRequest,
        silentAbort,
        startNewResponseBlock,
        openChat,
    } = useConciergeChat(
        {
            startNewChatAfter,
            includeContentSearch,
            includeHelpDocs,
            includeGlossaryTerms,
            includeCustomFaq,
            availableDomains,
            selectedDomains,
            isAppPanel,
            extraUrlParams,
            getContext,
            customInstructions,
        },
        visible,
    );

    const openTestSession = useOpenTestSession();
    const [newChatPending, setNewChatPending] = useState(false);
    // The chat being opened from Recent Chats; anything that takes the panel elsewhere drops it.
    const openingChatRef = useRef<string | null>(null);
    // Layout effect: the ref must already say "closed" when a replacement-session response lands in
    // the gap between the close commit and passive effects.
    const visibleRef = useRef(visible);
    useLayoutEffect(() => {
        visibleRef.current = visible;
    }, [visible]);

    // Inside a test session a new chat stays in test scope: a fresh session for the same model or
    // domain, which the session_uid effect below turns into a cleared dialog. Local state is left
    // alone until that session arrives, so a failed request keeps the current chat intact; a response
    // that lands after the panel was closed is dropped rather than re-opening it.
    const startNewChat = useCallback(() => {
        if (testSession) {
            if (newChatPending) return;
            setNewChatPending(true);
            openTestSession(testSessionScopeParams(testSession), { apply: () => visibleRef.current }).finally(() =>
                setNewChatPending(false),
            );
            return;
        }
        setAnsweredQuestionIds([]);
        openingChatRef.current = null;
        startNewChatBase();
    }, [startNewChatBase, testSession, openTestSession, newChatPending]);

    const activeClarificationData = useMemo((): {
        question: ClarificationQuestionItem;
        itemUid: string;
        questionIndex: number;
        totalQuestions: number;
        isLast: boolean;
    } | null => {
        for (const item of dialog) {
            const clarBlock = findClarificationBlock(item);
            if (!clarBlock) continue;

            const questions = getClarificationQuestions(clarBlock);
            const currentIdx = questions.findIndex((q) => !answeredQuestionIds.includes(q.question_id));
            if (currentIdx === -1) continue;

            return {
                question: questions[currentIdx],
                itemUid: item.uid,
                questionIndex: currentIdx + 1,
                totalQuestions: questions.length,
                isLast: currentIdx === questions.length - 1,
            };
        }
        return null;
    }, [dialog, answeredQuestionIds]);

    const clarifications = useClarificationsVisibility(
        issuesContext?.clarifyingQuestions ?? [],
        `${issuesTarget?.type ?? ''}:${issuesTarget?.id ?? 0}`,
    );
    // Also held back while the issues context is still loading: WelcomePage's
    // unmount resets defaultSelectedDomains to null, which empties its prompt
    // list for the rest of the session, so it must not mount only to be swapped
    // out for the clarifications a moment later.
    const hideWelcomePageForReportClarifications =
        (!!issuesTarget && issuesContext === null && issuesLoading) ||
        (!!issuesContext?.isEditAccess && clarifications.hasUnanswered);

    useEffect(() => {
        if ('concierge' !== activeTab || !welcomeDomainsRef.current) return;
        if (null === defaultSelectedDomains) dispatch(setDefaultSelectedDomains(welcomeDomainsRef.current.value));
        welcomeDomainsRef.current = null;
    }, [activeTab]);

    const handleClarificationAnswered = useCallback(
        (questionId: string, questionText: string, questionIndex: number, answer: string | null, isLast: boolean) => {
            setAnsweredQuestionIds((prev) => [...prev, questionId]);
            setDialog((prev) => {
                const newItems: typeof prev = [];
                if (questionIndex > 1) {
                    newItems.push({
                        uid: uid(),
                        type: 'response' as const,
                        parts: [{ type: 'markdown' as const, message: questionText, source: 'backend' as const }],
                        isClarificationQuestion: true,
                    });
                }
                newItems.push({
                    uid: uid(),
                    type: 'request' as const,
                    parts: [{ type: 'html' as const, message: answer ?? 'Skip', source: 'frontend' as const }],
                    isClarificationAnswer: true,
                });
                return [...prev, ...newItems];
            });
            // The backend holds the stream open while it waits for the answer; once the stream has
            // ended nothing will fill a continuation block, so none is opened.
            if (isLast && loading) {
                startNewResponseBlock();
            }
        },
        [startNewResponseBlock, loading],
    );

    // The chat reopened from Recent Chats while it stays the active one; its turn count tells the
    // restored transcript from turns asked since.
    const [openedChat, setOpenedChat] = useState<{
        conversationTs: string;
        title: string;
        memoryAvailable: boolean;
        restoredCount: number;
    } | null>(null);

    useEffect(() => {
        if (openedChat && String(activeChat) !== openedChat.conversationTs) {
            setOpenedChat(null);
        }
    }, [activeChat, openedChat]);

    const handleOpenChat = useCallback(
        (chat: IRecentChat) => {
            openingChatRef.current = chat.conversation_ts;
            conciergeAPI
                .getChatTranscript(chat.conversation_ts)
                .then(({ chat: stored, messages }) => {
                    if (openingChatRef.current !== chat.conversation_ts) return;
                    const restored = transcriptToDialog(stored.conversation_ts, messages);
                    // A clarification asked in a stored turn was settled then; it must not prompt again.
                    const settledQuestionIds = restored.dialog.flatMap((item) => {
                        const block = findClarificationBlock(item);
                        return block ? getClarificationQuestions(block).map((question) => question.question_id) : [];
                    });
                    if (testSession) dispatch(clearTestSession());
                    openChat(stored.conversation_ts, restored.dialog, restored.whyMessageUids);
                    setAnsweredQuestionIds(settledQuestionIds);
                    setOpenedChat({
                        conversationTs: stored.conversation_ts,
                        title: stored.title,
                        memoryAvailable: stored.memory_available,
                        restoredCount: restored.dialog.length,
                    });
                    setActiveTab('concierge');
                })
                .catch(() => toast('error', t('recent_chats.open_failed')))
                .finally(() => {
                    if (openingChatRef.current === chat.conversation_ts) openingChatRef.current = null;
                });
        },
        [openChat, testSession, dispatch, t],
    );

    // Anything that puts a question or a session in the panel shows the Concierge view and drops a
    // chat still being opened from the list.
    const showConciergeView = useCallback(() => {
        openingChatRef.current = null;
        setActiveTab('concierge');
    }, []);

    const handleChatRenamed = useCallback((chat: IRecentChat) => {
        setOpenedChat((current) =>
            current && current.conversationTs === chat.conversation_ts ? { ...current, title: chat.title } : current,
        );
    }, []);

    const handleChatDeleted = useCallback(
        (conversationTs: string) => {
            if (String(activeChat) === conversationTs) {
                onAbort();
                startNewChat();
            }
        },
        [activeChat, onAbort, startNewChat],
    );

    useEffect(() => {
        if (testSession) {
            showConciergeView();
            silentAbort();
            clearActiveChatRequest();
            setDialog([]);
            setAnsweredQuestionIds([]);
            setActiveChat(testSession.session_uid);
        }
    }, [testSession?.session_uid]);

    useEffect(() => {
        if (newChat !== null && domainsData && toolsData) {
            showConciergeView();
            silentAbort();
            clearActiveChatRequest();
            setDialog([]);
            setAnsweredQuestionIds([]);
            if (testSession) dispatch(clearTestSession());
            setActiveChat(Date.now());
            dispatch(clearNewChat());
            if (newChat) {
                onRequestSubmit(newChat);
            }
        }
    }, [
        newChat,
        domainsData,
        toolsData,
        clearActiveChatRequest,
        dispatch,
        silentAbort,
        onRequestSubmit,
        testSession,
        showConciergeView,
    ]);

    useEffect(() => {
        if (!visible) {
            showConciergeView();
        }
    }, [visible, showConciergeView]);

    useEffect(() => {
        if (!visible && testSession) {
            silentAbort();
            clearActiveChatRequest();
            dispatch(clearTestSession());
            setDialog([]);
            setAnsweredQuestionIds([]);
            setActiveChat(0);
        }
    }, [visible]);

    useEffect(() => {
        if (newRequestMessage && domainsData && toolsData) {
            showConciergeView();
            if (!activeChat) {
                setActiveChat(Date.now());
            }
            dispatch(clearNewRequestMessage());
            onRequestSubmit(newRequestMessage);
        }
    }, [newRequestMessage, domainsData, toolsData, activeChat, onRequestSubmit, dispatch, showConciergeView]);

    useEffect(() => {
        if (!testSession && prependContext?.showInChat && prependContext.text && dialog.length === 0 && activeChat) {
            const contextMessage = {
                uid: `prepend-context-${Date.now()}`,
                type: 'response' as const,
                parts: [
                    {
                        message: prependContext.text,
                        type: 'markdown' as const,
                        source: 'frontend' as const,
                    },
                ],
            };
            setDialog([contextMessage]);
        }
    }, [prependContext, dialog.length, activeChat, testSession]);

    const onClose = () => {
        setDialog([]);
        setAnsweredQuestionIds([]);
        setActiveChat(0);
        dispatch(setFromSearch(false));
        dispatch(hide());
        dispatch(setCloseChat(false));
        dispatch(clearNewChat());
        dispatch(setWaitingConcierge(false));
        dispatch(clearPrependContext());
        dispatch(clearTestSession());
    };

    useEffect(() => {
        if (closeChat) {
            onClose();
        }
    }, [closeChat]);

    useEffect(() => {
        dispatch(setCustomStyles(customStyles));
        dispatch(setIsAppPanel(isAppPanel));

        //@ts-ignore
        if (!window.tokenFormUpdatePropertyValue) {
            //@ts-ignore
            window.tokenFormUpdatePropertyValue = tokenFormUpdatePropertyValue;
        }

        return () => {
            //@ts-ignore
            delete window.tokenFormUpdatePropertyValue;
        };
    }, []);

    const resizeFrameRef = useRef<number | null>(null);
    const resizePauseRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const dragStartWidthRef = useRef(0);
    // Width under the cursor while a drag is in progress; null when no drag is.
    const dragWidthRef = useRef<number | null>(null);
    const emittedWidthRef = useRef<number | null>(null);

    const emitWidth = useCallback(
        (width: number, resizeState: 'dragging' | 'released') => {
            if (!isAppPanel) return;
            dispatchCustomEvent('mi-concierge-panel', {
                action: 'panel-visibility-changed',
                payload: { isOpen: true, width, resizeState },
            });
        },
        [isAppPanel],
    );

    const cancelResizeTimers = () => {
        if (null !== resizeFrameRef.current) {
            cancelAnimationFrame(resizeFrameRef.current);
            resizeFrameRef.current = null;
        }
        if (null !== resizePauseRef.current) {
            clearTimeout(resizePauseRef.current);
            resizePauseRef.current = null;
        }
    };

    useEffect(() => cancelResizeTimers, []);

    // Closed mid-drag: Resizable unmounts without onResizeStop, so the dragged width is saved here, before
    // the close is reported.
    useLayoutEffect(() => {
        if (visible || null === dragWidthRef.current) return;
        cancelResizeTimers();
        dispatch(setConciergeWidgetWidth(dragWidthRef.current));
        dragWidthRef.current = null;
    }, [visible]);

    return visible ? (
        <Stack sx={styles.container}>
            <Resizable
                defaultSize={{
                    width: store.getState().concierge.width,
                    height: '100%',
                }}
                className={'hierarchy-resize-container'}
                minWidth={400}
                maxWidth={660}
                onResizeStart={(event, direction, elementRef) => {
                    elementRef.classList.add('resizing-active');
                    dragStartWidthRef.current = elementRef.offsetWidth;
                    dragWidthRef.current = elementRef.offsetWidth;
                    emittedWidthRef.current = elementRef.offsetWidth;
                }}
                onResize={(event, direction, elementRef, delta) => {
                    dragWidthRef.current = Math.round(dragStartWidthRef.current + delta.width);
                    if (null === resizeFrameRef.current) {
                        resizeFrameRef.current = requestAnimationFrame(() => {
                            resizeFrameRef.current = null;
                            const width = dragWidthRef.current;
                            if (null === width || width === emittedWidthRef.current) return;
                            emittedWidthRef.current = width;
                            emitWidth(width, 'dragging');
                        });
                    }
                    // Page content follows only when the drag pauses: re-laying out a heavy page (grids) costs
                    // ~100 ms, which stalls the drag if done every frame.
                    if (null !== resizePauseRef.current) clearTimeout(resizePauseRef.current);
                    resizePauseRef.current = setTimeout(() => {
                        resizePauseRef.current = null;
                        if (null !== dragWidthRef.current) dispatch(setConciergeWidgetWidth(dragWidthRef.current));
                    }, RESIZE_PAUSE_MS);
                }}
                onResizeStop={(event, direction, elementRef) => {
                    cancelResizeTimers();
                    dragWidthRef.current = null;
                    elementRef.classList.remove('resizing-active');
                    dispatch(setConciergeWidgetWidth(elementRef.offsetWidth));
                    emitWidth(elementRef.offsetWidth, 'released');
                }}
                handleComponent={{
                    left: <Stack sx={styles.resizeHandler} />,
                }}
                handleStyles={{
                    left: styles.resizeStyle,
                }}
                enable={{
                    top: false,
                    right: false,
                    bottom: false,
                    left: true,
                    topRight: false,
                    bottomRight: false,
                    bottomLeft: false,
                    topLeft: false,
                }}
            >
                {!hideHeader && (
                    <Header
                        activeTab={activeTab}
                        setActiveTab={(tab: string) => {
                            openingChatRef.current = null;
                            if ('recent' === tab && 'concierge' === activeTab && null !== defaultSelectedDomains) {
                                welcomeDomainsRef.current = { value: defaultSelectedDomains };
                            }
                            setActiveTab(tab);
                        }}
                        setLastInteraction={setLastInteraction}
                        botIcon={botIcon}
                        openedChatTitle={openedChat?.title ?? null}
                    />
                )}
                {activeTab == 'concierge' && !isDomainsLoading && !hasDomains && !testSession && (
                    <Stack sx={{ flex: 1, alignItems: 'center', justifyContent: 'center', p: 3 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                textAlign: 'center',
                                gap: 1,
                                p: 3,
                                borderRadius: '8px',
                                border: '1px solid',
                                borderColor: 'divider',
                                backgroundColor: (theme) =>
                                    theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
                            }}
                        >
                            <Box sx={{ fontSize: '32px', color: 'warning.main', lineHeight: 1 }}>
                                <IconMi icon="info-circle" />
                            </Box>
                            <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>{t('no_sources_title')}</Typography>
                            <Typography sx={{ fontSize: '13px', color: 'text.secondary', maxWidth: '280px' }}>
                                {t('no_sources')}
                            </Typography>
                        </Box>
                    </Stack>
                )}
                {activeTab == 'concierge' && visible && !testSession && issuesTarget?.viewer && (
                    <PanelInsightCard elementId={issuesTarget.id} insightId={issuesTarget.viewer.insightId} />
                )}
                {activeTab == 'concierge' &&
                    issuesContext &&
                    !testSession &&
                    ('element' === issuesTarget?.type ? (
                        <ReportConciergeContent
                            context={issuesContext}
                            onAnswer={answerQuestion}
                            onSkip={skipQuestion}
                            onCloseClarifications={clarifications.close}
                            clarificationsVisible={clarifications.visible}
                        />
                    ) : (
                        <ClarificationsSection
                            context={issuesContext}
                            onAnswer={answerQuestion}
                            onSkip={skipQuestion}
                            onClose={clarifications.close}
                            visible={clarifications.visible}
                            objectType={'dataset' === issuesTarget?.type ? 'dataset' : 'domain'}
                        />
                    ))}
                {activeTab == 'concierge' && (isDomainsLoading || hasDomains || !!testSession) && (
                    <>
                        {testSession && <TestSessionBanner session={testSession} />}
                        {!activeChat ? (
                            hideWelcomePageForReportClarifications ? (
                                // Keeps a growing child in the panel column so the footer
                                // stays pinned to the bottom while the welcome page is hidden.
                                <Box sx={{ flexGrow: 1 }} />
                            ) : (
                                <WelcomePage
                                    setActiveChat={setActiveChat}
                                    setActiveChatRequest={onRequestSubmit}
                                    welcomeIcon={welcomeIcon}
                                    suggestedPrompts={suggestedPrompts}
                                    title={title}
                                    description={description}
                                    welcomeIconWidth={welcomeIconWidth}
                                    welcomeIconHeight={welcomeIconHeight}
                                />
                            )
                        ) : (
                            <ActiveChat
                                key={activeChat}
                                dialog={dialog}
                                id={activeChat}
                                loading={loading}
                                showSearchRequest={showSearchRequest}
                                botIcon={botIcon}
                                loadMore={loadMore}
                                onUpdateDialog={setDialog}
                                useInfoButton={useInfoButton}
                                onResendRequest={onResendRequest}
                                onRetryError={onRetryError}
                                onAbort={onAbort}
                                onAction={onAction}
                            />
                        )}
                        {openedChat && !openedChat.memoryAvailable && dialog.length === openedChat.restoredCount && (
                            <Typography data-test="concierge_chat_memory_expired" sx={styles.memoryNote}>
                                {t('recent_chats.memory_expired')}
                            </Typography>
                        )}
                        {activeClarificationData ? (
                            <ClarificationQuestionComponent
                                questionId={activeClarificationData.question.question_id}
                                question={activeClarificationData.question.question}
                                options={activeClarificationData.question.options}
                                optionDetails={activeClarificationData.question.option_details}
                                botIcon={botIcon}
                                questionIndex={activeClarificationData.questionIndex}
                                totalQuestions={activeClarificationData.totalQuestions}
                                onAnswered={(answer) =>
                                    handleClarificationAnswered(
                                        activeClarificationData.question.question_id,
                                        activeClarificationData.question.question,
                                        activeClarificationData.questionIndex,
                                        answer,
                                        activeClarificationData.isLast,
                                    )
                                }
                            />
                        ) : (
                            <Footer
                                activeChat={activeChat}
                                setActiveChat={setActiveChat}
                                setActiveChatRequest={onRequestSubmit}
                                startNewChat={startNewChat}
                                startNewChatPending={newChatPending}
                                onAbort={onAbort}
                                loading={loading}
                                loadingToSendNewRequestMessage={newRequestMessage && !domainsData && !toolsData}
                                showSourceSelector={showSourceSelector}
                                displayModelName={displayModelName}
                                autoFocus={autoFocus}
                            />
                        )}
                    </>
                )}
                {activeTab == 'recent' && (
                    <RecentChats
                        onOpenChat={handleOpenChat}
                        onChatRenamed={handleChatRenamed}
                        onChatDeleted={handleChatDeleted}
                    />
                )}
            </Resizable>
        </Stack>
    ) : null;
}
